/**
 * threadDisplayTitle — ONE rule for what a thread is called on every list
 * surface and in the header (chat-thread experience PR-5, 2026-09-09; names
 * made PERSONAL 2026-09-09: a rename is the viewer's own, on every device,
 * and nobody else's list changes).
 *
 * Precedence, highest first:
 *   1. a ROUTINE thread is called by its routine name — the caller passes it
 *      (routineThreadDisplayName); this module never imports the store;
 *   2. the viewer's OWN rename (`userRenamed` on their record) — a name they
 *      chose beats everything below it;
 *   3. a DIRECT MESSAGE is called by the other person: exactly two humans in
 *      the thread (Prest0n's presence is irrelevant — it is not a participant
 *      record) and the viewer is one of them ⇒ the counterpart's display name;
 *   4. the record's own title (the auto label / the canonical creator title);
 *   5. the caller's fallback (never empty).
 *
 * Pure and dependency-free: the rail, the chip strip and the mobile sheet all
 * call it, so the three can never disagree about a thread's name.
 */

export interface DisplayTitleThread {
  id: string;
  title?: string;
  userRenamed?: boolean;
}

export type ThreadDisplayKind = 'direct' | 'group' | 'individual';

export interface ResolveThreadDisplayTitleArgs {
  thread: DisplayTitleThread;
  /** The viewer. */
  selfId?: string | null;
  /** The thread's registered participants (uids), when the membership read named them. */
  participantIds?: readonly string[] | null;
  /** uid → display name (roster + server-resolved names). */
  memberLabels?: Readonly<Record<string, string>> | null;
  /** A routine thread's name (wins outright). */
  routineName?: string | null;
  /** What to say when nothing else names the thread. */
  fallback?: string;
}

const DEFAULT_FALLBACK = 'New thread';

function trim(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function distinctIds(ids: readonly string[] | null | undefined): string[] {
  const out: string[] = [];
  for (const raw of Array.isArray(ids) ? ids : []) {
    const id = trim(raw);
    if (id && !out.includes(id)) out.push(id);
  }
  return out;
}

/**
 * Exactly two humans, the viewer one of them. A thread whose participants are
 * not known yet (no ids) is never a direct message — the rail cannot name the
 * other person, so it must not promise one.
 */
export function isDirectMessageThread(
  participantIds: readonly string[] | null | undefined,
  selfId: string | null | undefined,
): boolean {
  const self = trim(selfId);
  if (!self) return false;
  const ids = distinctIds(participantIds);
  return ids.length === 2 && ids.includes(self);
}

/** The other person in a direct message; null when the thread is not one. */
export function directMessageCounterpart(
  participantIds: readonly string[] | null | undefined,
  selfId: string | null | undefined,
): string | null {
  if (!isDirectMessageThread(participantIds, selfId)) return null;
  const self = trim(selfId);
  return distinctIds(participantIds).find((id) => id !== self) ?? null;
}

/**
 * Which kind of row a thread is: 'direct' when the ids prove a two-person
 * thread with the viewer in it; 'group' when the registry counts two or more
 * participants (ids known or not — the count is the header's own source);
 * 'individual' otherwise. A thread with no registry row counts as 1.
 */
export function resolveThreadDisplayKind(args: {
  participantIds?: readonly string[] | null;
  participantCount?: number | null;
  selfId?: string | null;
  /** The server-owned shared marker on the index doc (a just-pushed thread). */
  sharedThread?: boolean;
}): ThreadDisplayKind {
  if (isDirectMessageThread(args.participantIds, args.selfId)) return 'direct';
  const ids = distinctIds(args.participantIds);
  const count = Number.isFinite(args.participantCount as number)
    ? Math.max(0, Math.floor(args.participantCount as number))
    : 0;
  if (ids.length >= 2 || count >= 2 || args.sharedThread === true) return 'group';
  return 'individual';
}

/** The name a direct message wears for this viewer, or null. */
export function directMessageTitle(args: {
  participantIds?: readonly string[] | null;
  selfId?: string | null;
  memberLabels?: Readonly<Record<string, string>> | null;
}): string | null {
  const other = directMessageCounterpart(args.participantIds, args.selfId);
  if (!other) return null;
  const label = trim(args.memberLabels?.[other]);
  // An id is not a name: with no label the thread keeps its own title rather
  // than wearing a 28-character uid.
  return label && label !== other ? label : null;
}

export function resolveThreadDisplayTitle(args: ResolveThreadDisplayTitleArgs): string {
  const fallback = trim(args.fallback) || DEFAULT_FALLBACK;
  const routine = trim(args.routineName);
  if (routine) return routine;
  const own = trim(args.thread?.title);
  if (args.thread?.userRenamed === true && own) return own;
  const direct = directMessageTitle(args);
  if (direct) return direct;
  return own || fallback;
}
